
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface ContaReceber {
  _id: string
  cliente: string
  valor: number
  vencimento: string
  status: 'pendente' | 'vencido' | 'recebido' | 'cancelado'
  origem: 'crm' | 'orcamento' | 'contrato' | 'manual'
  descricao: string
  obra_id?: string
  parcela?: number
  total_parcelas?: number
}

interface ContaPagar {
  _id: string
  fornecedor: string
  categoria: 'mao_obra' | 'material' | 'administrativo' | 'tributario' | 'equipamentos' | 'financeiro'
  valor: number
  vencimento: string
  status: 'pendente' | 'vencido' | 'pago' | 'cancelado'
  descricao: string
  obra_id?: string
  compra_id?: string
  centro_custo?: string
}

interface FluxoCaixa {
  _id: string
  data: string
  tipo: 'entrada' | 'saida'
  valor: number
  categoria: string
  descricao: string
  origem: 'manual' | 'automatico'
  obra_id?: string
  conta_id?: string
  status: 'realizado' | 'projecao'
}

interface MovimentacaoBancaria {
  _id: string
  data: string
  tipo: 'entrada' | 'saida' | 'transferencia'
  valor: number
  conta_bancaria: string
  conta_destino?: string
  categoria: 'mao_obra' | 'material' | 'administrativo' | 'tributario' | 'equipamentos' | 'financeiro' | 'recebimento' | 'outros'
  obra_id?: string
  centro_custo?: string
  descricao: string
  documento?: string
  forma_pagamento?: string
  status: 'pendente' | 'processando' | 'concluido' | 'cancelado'
  observacoes?: string
  anexo_url?: string
  conciliado?: boolean
}

interface MetricasFinanceiras {
  receitasMes: number
  despesasMes: number
  saldoAtual: number
  contasReceber: number
  contasPagar: number
  fluxoCaixa: number
  margemLucro: number
  totalObras: number
  comparativoMesAnterior: {
    receitas: number
    despesas: number
    saldo: number
  }
}

interface DadosGraficos {
  fluxoCaixaMensal: Array<{
    mes: string
    entradas: number
    saidas: number
    saldo: number
  }>
  evolucaoPatrimonial: Array<{
    mes: string
    patrimonio: number
  }>
  distribuicaoGastos: Array<{
    categoria: string
    valor: number
    percentual: number
  }>
  previsaoRecebimentos: Array<{
    data: string
    valor: number
    cliente: string
  }>
}

export const useFinanceiro = () => {
  const [contasReceber, setContasReceber] = useState<ContaReceber[]>([])
  const [contasPagar, setContasPagar] = useState<ContaPagar[]>([])
  const [fluxoCaixa, setFluxoCaixa] = useState<FluxoCaixa[]>([])
  const [movimentacoesBancarias, setMovimentacoesBancarias] = useState<MovimentacaoBancaria[]>([])
  const [dadosGraficos, setDadosGraficos] = useState<DadosGraficos>({
    fluxoCaixaMensal: [],
    evolucaoPatrimonial: [],
    distribuicaoGastos: [],
    previsaoRecebimentos: []
  })
  const [metricas, setMetricas] = useState<MetricasFinanceiras>({
    receitasMes: 0,
    despesasMes: 0,
    saldoAtual: 0,
    contasReceber: 0,
    contasPagar: 0,
    fluxoCaixa: 0,
    margemLucro: 0,
    totalObras: 0,
    comparativoMesAnterior: {
      receitas: 0,
      despesas: 0,
      saldo: 0
    }
  })
  const [loading, setLoading] = useState(false)

  // Buscar contas a receber
  const fetchContasReceber = useCallback(async () => {
    try {
      const { list } = await lumi.entities.contas_receber.list()
      setContasReceber(list || [])
    } catch (error) {
      console.error('Erro ao buscar contas a receber:', error)
    }
  }, [])

  // Buscar contas a pagar
  const fetchContasPagar = useCallback(async () => {
    try {
      const { list } = await lumi.entities.contas_pagar.list()
      setContasPagar(list || [])
    } catch (error) {
      console.error('Erro ao buscar contas a pagar:', error)
    }
  }, [])

  // Buscar fluxo de caixa
  const fetchFluxoCaixa = useCallback(async () => {
    try {
      const { list } = await lumi.entities.fluxo_caixa.list()
      setFluxoCaixa(list || [])
    } catch (error) {
      console.error('Erro ao buscar fluxo de caixa:', error)
    }
  }, [])

  // Buscar movimentações bancárias
  const fetchMovimentacoesBancarias = useCallback(async () => {
    try {
      const { list } = await lumi.entities.movimentacoes_bancarias.list()
      setMovimentacoesBancarias(list || [])
    } catch (error) {
      console.error('Erro ao buscar movimentações bancárias:', error)
    }
  }, [])

  // Gerar dados para gráficos baseado nas movimentações bancárias
  const gerarDadosGraficos = useCallback(() => {
    if (movimentacoesBancarias.length === 0) {
      setDadosGraficos({
        fluxoCaixaMensal: [],
        evolucaoPatrimonial: [],
        distribuicaoGastos: [],
        previsaoRecebimentos: contasReceber
          .filter(conta => conta.status === 'pendente')
          .map(conta => ({
            data: conta.vencimento,
            valor: conta.valor,
            cliente: conta.cliente
          }))
          .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())
      })
      return
    }

    // 1. Fluxo de Caixa Mensal - últimos 6 meses
    const hoje = new Date()
    const mesesLabels = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    const fluxoCaixaMensal = []

    for (let i = 5; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      const mes = data.getMonth()
      const ano = data.getFullYear()
      
      const movimentacoesMes = movimentacoesBancarias.filter(mov => {
        const dataMov = new Date(mov.data)
        return dataMov.getMonth() === mes && 
               dataMov.getFullYear() === ano && 
               mov.status === 'concluido'
      })

      const entradas = movimentacoesMes
        .filter(mov => mov.tipo === 'entrada')
        .reduce((total, mov) => total + mov.valor, 0)

      const saidas = movimentacoesMes
        .filter(mov => mov.tipo === 'saida')
        .reduce((total, mov) => total + mov.valor, 0)

      fluxoCaixaMensal.push({
        mes: mesesLabels[mes],
        entradas,
        saidas,
        saldo: entradas - saidas
      })
    }

    // 2. Evolução Patrimonial - saldo acumulado
    const evolucaoPatrimonial = []
    let saldoAcumulado = 0

    for (let i = 5; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      const mes = data.getMonth()
      const ano = data.getFullYear()
      
      const movimentacoesMes = movimentacoesBancarias.filter(mov => {
        const dataMov = new Date(mov.data)
        return dataMov.getMonth() === mes && 
               dataMov.getFullYear() === ano && 
               mov.status === 'concluido'
      })

      const saldoMes = movimentacoesMes.reduce((total, mov) => {
        return mov.tipo === 'entrada' ? total + mov.valor : total - mov.valor
      }, 0)

      saldoAcumulado += saldoMes

      evolucaoPatrimonial.push({
        mes: mesesLabels[mes],
        patrimonio: saldoAcumulado
      })
    }

    // 3. Distribuição de Gastos - últimos 3 meses por categoria
    const dataInicio = new Date(hoje.getFullYear(), hoje.getMonth() - 2, 1)
    const gastosCategoria = movimentacoesBancarias
      .filter(mov => {
        const dataMov = new Date(mov.data)
        return dataMov >= dataInicio && 
               mov.tipo === 'saida' && 
               mov.status === 'concluido'
      })
      .reduce((acc, mov) => {
        const categoria = mov.categoria
        if (!acc[categoria]) {
          acc[categoria] = 0
        }
        acc[categoria] += mov.valor
        return acc
      }, {} as Record<string, number>)

    const totalGastos = Object.values(gastosCategoria).reduce((total, valor) => total + valor, 0)
    
    const distribuicaoGastos = Object.entries(gastosCategoria)
      .map(([categoria, valor]) => ({
        categoria,
        valor,
        percentual: totalGastos > 0 ? (valor / totalGastos) * 100 : 0
      }))
      .sort((a, b) => b.valor - a.valor)

    // 4. Previsão de Recebimentos
    const previsaoRecebimentos = contasReceber
      .filter(conta => conta.status === 'pendente')
      .map(conta => ({
        data: conta.vencimento,
        valor: conta.valor,
        cliente: conta.cliente
      }))
      .sort((a, b) => new Date(a.data).getTime() - new Date(b.data).getTime())

    setDadosGraficos({
      fluxoCaixaMensal,
      evolucaoPatrimonial,
      distribuicaoGastos,
      previsaoRecebimentos
    })
  }, [movimentacoesBancarias, contasReceber])

  // Calcular métricas financeiras
  const calcularMetricas = useCallback(() => {
    const hoje = new Date()
    const mesAtual = hoje.getMonth()
    const anoAtual = hoje.getFullYear()
    const mesAnterior = mesAtual === 0 ? 11 : mesAtual - 1
    const anoAnterior = mesAtual === 0 ? anoAtual - 1 : anoAtual

    // Usar movimentações bancárias para receitas e despesas realizadas
    const movimentacoesRealizadas = movimentacoesBancarias.filter(mov => mov.status === 'concluido')

    // Receitas do mês atual
    const receitasMes = movimentacoesRealizadas
      .filter(mov => {
        const dataMov = new Date(mov.data)
        return mov.tipo === 'entrada' && 
               dataMov.getMonth() === mesAtual && 
               dataMov.getFullYear() === anoAtual
      })
      .reduce((total, mov) => total + mov.valor, 0)

    // Despesas do mês atual
    const despesasMes = movimentacoesRealizadas
      .filter(mov => {
        const dataMov = new Date(mov.data)
        return mov.tipo === 'saida' && 
               dataMov.getMonth() === mesAtual && 
               dataMov.getFullYear() === anoAtual
      })
      .reduce((total, mov) => total + mov.valor, 0)

    // Saldo atual baseado em todas as movimentações realizadas
    const saldoAtual = movimentacoesRealizadas
      .reduce((total, mov) => {
        return mov.tipo === 'entrada' ? total + mov.valor : total - mov.valor
      }, 0)

    // Contas a receber (pendentes)
    const totalContasReceber = contasReceber
      .filter(conta => conta.status === 'pendente' || conta.status === 'vencido')
      .reduce((total, conta) => total + conta.valor, 0)

    // Contas a pagar (pendentes)
    const totalContasPagar = contasPagar
      .filter(conta => conta.status === 'pendente' || conta.status === 'vencido')
      .reduce((total, conta) => total + conta.valor, 0)

    // Fluxo de caixa projetado (mantém do fluxo_caixa para projeções)
    const fluxoCaixaProjetado = fluxoCaixa
      .filter(item => item.status === 'projecao')
      .reduce((total, item) => {
        return item.tipo === 'entrada' ? total + item.valor : total - item.valor
      }, 0)

    // Margem de lucro
    const margemLucro = receitasMes > 0 ? ((receitasMes - despesasMes) / receitasMes) * 100 : 0

    // Total em obras (valor das contas a receber por obra)
    const totalObras = contasReceber
      .filter(conta => conta.obra_id)
      .reduce((total, conta) => total + conta.valor, 0)

    // Comparativo mês anterior usando movimentações bancárias
    const receitasMesAnterior = movimentacoesRealizadas
      .filter(mov => {
        const dataMov = new Date(mov.data)
        return mov.tipo === 'entrada' && 
               dataMov.getMonth() === mesAnterior && 
               dataMov.getFullYear() === anoAnterior
      })
      .reduce((total, mov) => total + mov.valor, 0)

    const despesasMesAnterior = movimentacoesRealizadas
      .filter(mov => {
        const dataMov = new Date(mov.data)
        return mov.tipo === 'saida' && 
               dataMov.getMonth() === mesAnterior && 
               dataMov.getFullYear() === anoAnterior
      })
      .reduce((total, mov) => total + mov.valor, 0)

    setMetricas({
      receitasMes,
      despesasMes,
      saldoAtual,
      contasReceber: totalContasReceber,
      contasPagar: totalContasPagar,
      fluxoCaixa: fluxoCaixaProjetado,
      margemLucro,
      totalObras,
      comparativoMesAnterior: {
        receitas: receitasMesAnterior,
        despesas: despesasMesAnterior,
        saldo: receitasMesAnterior - despesasMesAnterior
      }
    })
  }, [contasReceber, contasPagar, fluxoCaixa, movimentacoesBancarias])

  // Criar conta a receber
  const criarContaReceber = async (dadosConta: Omit<ContaReceber, '_id'>) => {
    try {
      const novaConta = await lumi.entities.contas_receber.create({
        ...dadosConta,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'admin'
      })
      await fetchContasReceber()
      toast.success('Conta a receber criada com sucesso!')
      return novaConta
    } catch (error) {
      console.error('Erro ao criar conta a receber:', error)
      toast.error('Erro ao criar conta a receber')
      throw error
    }
  }

  // Atualizar status conta a receber
  const atualizarStatusContaReceber = async (contaId: string, novoStatus: ContaReceber['status']) => {
    try {
      await lumi.entities.contas_receber.update(contaId, {
        status: novoStatus,
        updatedAt: new Date().toISOString()
      })
      await fetchContasReceber()
      toast.success('Status atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      toast.error('Erro ao atualizar status')
      throw error
    }
  }

  // Criar conta a pagar
  const criarContaPagar = async (dadosConta: Omit<ContaPagar, '_id'>) => {
    try {
      const novaConta = await lumi.entities.contas_pagar.create({
        ...dadosConta,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: 'admin'
      })
      await fetchContasPagar()
      toast.success('Conta a pagar criada com sucesso!')
      return novaConta
    } catch (error) {
      console.error('Erro ao criar conta a pagar:', error)
      toast.error('Erro ao criar conta a pagar')
      throw error
    }
  }

  // Atualizar status conta a pagar
  const atualizarStatusContaPagar = async (contaId: string, novoStatus: ContaPagar['status']) => {
    try {
      await lumi.entities.contas_pagar.update(contaId, {
        status: novoStatus,
        updatedAt: new Date().toISOString()
      })
      await fetchContasPagar()
      toast.success('Status atualizado com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      toast.error('Erro ao atualizar status')
      throw error
    }
  }

  // Carregar todos os dados
  const carregarDados = useCallback(async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchContasReceber(),
        fetchContasPagar(),
        fetchFluxoCaixa(),
        fetchMovimentacoesBancarias()
      ])
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error)
      toast.error('Erro ao carregar dados financeiros')
    } finally {
      setLoading(false)
    }
  }, [fetchContasReceber, fetchContasPagar, fetchFluxoCaixa, fetchMovimentacoesBancarias])

  // Efeitos
  useEffect(() => {
    carregarDados()
  }, [carregarDados])

  useEffect(() => {
    calcularMetricas()
  }, [calcularMetricas])

  useEffect(() => {
    gerarDadosGraficos()
  }, [gerarDadosGraficos])

  return {
    // Estados
    contasReceber,
    contasPagar,
    fluxoCaixa,
    movimentacoesBancarias,
    dadosGraficos,
    metricas,
    loading,
    
    // Funções
    carregarDados,
    criarContaReceber,
    atualizarStatusContaReceber,
    criarContaPagar,
    atualizarStatusContaPagar,
    fetchContasReceber,
    fetchContasPagar,
    fetchFluxoCaixa,
    fetchMovimentacoesBancarias
  }
}
